import { useEffect, useRef, useState } from 'react'

import { IconBuildingBank, IconChartPie, IconCircleCheck, IconPlus } from '@tabler/icons-react'

import { type Account, AccountInclusionStatus, type Connection } from '@subaio/api-types/financedata'
import { ButtonGroup, FormattedMessage, useDebouncedCallback } from '@subaio/components'

import { ButtonSolid } from 'components/Buttons'

import { Bank } from './Bank'
import { BottomContent } from './BottomContent'
import { Card } from './Card'
import { Description } from './Description'
import { Subtitle } from './Subtitle'
import { Title } from './Title'
import { TitleRow } from './TitleRow'

type Props = {
	isDemoData: boolean
	connections: Array<Connection>
	accounts: Array<Account>

	updateAccountInclusion: (accountsMap: Record<string, boolean>) => Promise<void>
	openCollectionFlow: () => Promise<void>
	removeConnection: (connectionId: string) => Promise<void>
}

export const Connections = ({
	isDemoData,
	connections,
	accounts,
	updateAccountInclusion,
	openCollectionFlow,
	removeConnection,
}: Props) => {
	const [selectedAccountIds, setSelectedAccountIds] = useState(() =>
		accounts
			.filter((account) => account.inclusionStatus === AccountInclusionStatus.IncludedByUser)
			.map((account) => account.id),
	)
	const pendingAccountInclusions = useRef<Record<string, boolean>>({})
	const accountInclusionUpdate = useRef(Promise.resolve())
	const updateAccountInclusionDebounced = useDebouncedCallback(() => {
		const accountsMap = pendingAccountInclusions.current
		pendingAccountInclusions.current = {}
		accountInclusionUpdate.current = accountInclusionUpdate.current
			.catch(() => undefined)
			.then(() => updateAccountInclusion(accountsMap))
	}, 500)

	useEffect(() => {
		setSelectedAccountIds(
			accounts
				.filter((account) => account.inclusionStatus === AccountInclusionStatus.IncludedByUser)
				.map((account) => account.id),
		)
	}, [accounts])

	const connectionsWithAccounts = connections.map((connection) => ({
		...connection,
		accounts: accounts.filter((account) => account.connection?.id === connection.id),
	}))

	return (
		<>
			<Subtitle>
				{isDemoData ? (
					<FormattedMessage id='home.connections.demo.subtitle' defaultMessage='Your demo persona is ready' />
				) : (
					<FormattedMessage id='home.connections.subtitle' defaultMessage='Your financial data is connected' />
				)}
				<IconCircleCheck size={22} strokeWidth={1.6} />
			</Subtitle>
			<Card>
				<TitleRow>
					{isDemoData ? <IconChartPie /> : <IconBuildingBank />}
					<Title>
						{isDemoData ? (
							<FormattedMessage id='home.connections.demo.title' defaultMessage='Connected demo persona' />
						) : (
							<FormattedMessage id='home.connections.title' defaultMessage='Connected banks' />
						)}
					</Title>
				</TitleRow>
				<Description>
					{isDemoData ? (
						<FormattedMessage
							id='home.connections.demo.description'
							defaultMessage="View the demo personas you've connected to and add more whenever you need to."
						/>
					) : (
						<FormattedMessage
							id='home.connections.description'
							defaultMessage="View the banks you've already connected and add more whenever you need to."
						/>
					)}
				</Description>
				<div>
					{connectionsWithAccounts.map((connection) => (
						<Bank
							key={connection.id}
							connection={connection}
							selectedAccountIds={selectedAccountIds}
							updateAccountInclusion={(accountsMap) => {
								setSelectedAccountIds((selectedAccountIds) => [
									...selectedAccountIds.filter((id) => !Object.keys(accountsMap).includes(id)),
									...Object.entries(accountsMap)
										.filter(([, isSelected]) => isSelected)
										.map(([id]) => id),
								])
								pendingAccountInclusions.current = { ...pendingAccountInclusions.current, ...accountsMap }
								updateAccountInclusionDebounced()
								return Promise.resolve()
							}}
							removeConnection={removeConnection}
						/>
					))}
				</div>
				<BottomContent>
					<ButtonGroup center>
						<ButtonSolid
							size='small'
							label={
								<>
									{isDemoData ? (
										<FormattedMessage id='home.demo.buttonAddBanks' defaultMessage='Add another persona' />
									) : (
										<FormattedMessage id='home.buttonAddBanks' defaultMessage='Add another bank' />
									)}
									<IconPlus strokeWidth={1.2} />
								</>
							}
							onClick={openCollectionFlow}
						/>
					</ButtonGroup>
				</BottomContent>
			</Card>
		</>
	)
}
